<script lang="ts">
import type { CommandPaletteEmits, CommandPaletteGroup, CommandPaletteItem, CommandPaletteProps, CommandPaletteSlots, PopoverEmits, PopoverProps, PopoverSlots } from '#ui/types';

export interface CommandPopoverProps<T extends CommandPaletteItem, G extends CommandPaletteGroup<T>> extends CommandPaletteProps<G, T>, Pick<PopoverProps, 'open'> {
	popover?: Omit<Partial<PopoverProps>, 'open'>;
	content?: PopoverProps['content'];
}

export type CommandPopoverSlots<T extends CommandPaletteItem, G extends CommandPaletteGroup<T>> = CommandPaletteSlots<T, G> & {
	'default': PopoverSlots['default'];
	'anchor': PopoverSlots['anchor'];
	'content-top': () => any;
	'content-bottom': () => any;
};
</script>

<script setup lang="ts" generic="T extends CommandPaletteItem, G extends CommandPaletteGroup<T>">
import { controlledComputed, reactiveOmit, useMutationObserver, useResizeObserver } from '@vueuse/core';
import { useForwardPropsEmits } from 'reka-ui';
import { computed, useTemplateRef } from 'vue';

import { omit, pick } from '#layers/base/utils/object';
import { mergeUi } from '#layers/ui/utils/merge-ui';

const props = withDefaults(defineProps<CommandPopoverProps<T, G>>(), {
	content: () => ({ side: 'bottom', align: 'start' }),
});

const emits = defineEmits<CommandPaletteEmits<T> & Pick<PopoverEmits, 'update:open'>>();
const slots = defineSlots<CommandPopoverSlots<T, G>>();
const open = defineModel<boolean>('open');

const popoverSlots = pick(slots, ['default', 'anchor']);

const commandPaletteForwarded = useForwardPropsEmits(reactiveOmit(props, 'popover', 'content', 'open'), emits);
const commandPaletteSlots = omit(slots, ['default', 'anchor']);

const wrapperRef = useTemplateRef('wrapper');

useDynamicListBoxHeight();

function useDynamicListBoxHeight() {
	const viewport = computed<HTMLDivElement | undefined>(() => {
		if (!wrapperRef.value) return;

		const rootEl = wrapperRef.value.querySelector('.command-palette-root') as HTMLDivElement;
		if (!rootEl) return;

		const viewportEl = rootEl.querySelector('div[role="presentation"]') as HTMLDivElement;
		if (viewportEl) return viewportEl;

		return rootEl.querySelector('div') as HTMLDivElement;
	});

	const listBox = controlledComputed(viewport, () => {
		return viewport.value?.parentElement as HTMLDivElement;
	});

	useResizeObserver(viewport, () => {
		requestAnimationFrame(() => {
			if (!viewport.value) return;

			const height = viewport.value.offsetHeight;
			listBox.value?.style.setProperty('--cmd-list-height', `${height.toFixed(0)}px`);
		});
	});

	// use a mutation observer to react to the empty state replacing the listbox and vice versa
	useMutationObserver(viewport, () => {
		listBox.trigger();
	}, { childList: true });
}
</script>

<template>
	<DPopover v-model:open="open" :content v-bind="popover">
		<template v-for="(_, name) in popoverSlots" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>

		<template #content>
			<div ref="wrapper" class="flex max-h-(--reka-popper-available-height) flex-col">
				<slot name="content-top" />
				<DCommandPalette
					v-bind="commandPaletteForwarded"
					:ui="mergeUi({
						root: 'command-palette-root p-0',
						content: 'h-(--cmd-list-height) motion-safe:transition-height duration-75 overflow-auto overscroll-contain',
						viewport: 'flex-none',
						input: '[&>input]:my-1.5 [&>input]:ps-3.5 [&>input]:pe-3 [&>input]:h-7 [&>span]:hidden [&>input]:truncate',
						group: 'p-1',
						item: 'ps-2.5 pe-1 py-1.5 text-sm',
						itemLeadingIcon: 'size-4',
						label: 'px-2.5',
						empty: 'py-2.5 text-start px-3',
					} as CommandPopoverProps<T, G>['ui'], props.ui)"
				>
					<template v-for="(_, name) in commandPaletteSlots" #[name]="slotData: any">
						<slot :name v-bind="slotData" />
					</template>
				</DCommandPalette>
				<slot name="content-bottom" />
			</div>
		</template>
	</DPopover>
</template>
